package no.vareflow.app.domain

// Locale-dependent formatting is supplied per platform (jvmMain / jsMain).
// Everything else here is plain Kotlin and behaves the same everywhere.

/** Compares names the Norwegian way (æ, ø, å after z), ignoring case and accents. */
expect fun compareNorwegian(a: String, b: String): Int

fun sortNorwegian(values: List<String>): List<String> =
    values.sortedWith { a, b -> compareNorwegian(a, b) }

/** Whole prices without decimals, otherwise two decimals. */
expect fun formatPrice(value: Double): String

/** `dd.MM.yyyy HH:mm` in the device's time zone. */
expect fun formatDateTime(time: Long): String

/** `HH:mm` in the device's time zone. */
expect fun formatTime(time: Long): String

/** Up to three decimals with a Norwegian decimal comma, no grouping. */
expect fun formatQuantity(value: Double): String

/** The current month as `YYYY-MM` in the device's time zone. */
expect fun currentYearMonth(): String

private val NORWEGIAN_MONTHS = listOf(
    "Januar", "Februar", "Mars", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Desember"
)

private val ENGLISH_MONTHS = listOf(
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
)

/** "2026-10" → "Oktober 2026" (or "October 2026"); anything that is not `YYYY-MM` is returned unchanged. */
fun formatYearMonth(value: String, language: AppLanguage = I18n.language): String =
    YearMonthValue.parse(value)?.let {
        val names = if (language == AppLanguage.EN) ENGLISH_MONTHS else NORWEGIAN_MONTHS
        "${names[it.month - 1]} ${it.year}"
    } ?: value

/**
 * Month to suggest for a new inventory session: the current month, or the month after the latest
 * existing one when that is already current or in the future. Invalid entries are ignored.
 */
fun nextSuggestedYearMonth(
    existing: List<String>,
    now: String = currentYearMonth()
): String {
    val current = YearMonthValue.parse(now) ?: return now
    val latest = existing.mapNotNull { YearMonthValue.parse(it) }.maxOrNull()
    return when {
        latest == null -> current.toString()
        latest < current -> current.toString()
        else -> latest.plusMonths(1).toString()
    }
}

fun locationIdFromName(name: String): String {
    val slug = name.lowercase()
        .replace("æ", "ae")
        .replace("ø", "o")
        .replace("å", "a")
        .replace(Regex("[^a-z0-9]+"), "-")
        .trim('-')
        .ifBlank { "location" }
    return slug
}
